
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heart, Shield, Users, TrendingUp, Check, Building2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { redirectToAuth, checkAuthToken } from '@/utils/authUtils';
import { useCallback } from 'react';

export const MarketingHomepage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleAuthAction = useCallback(() => {
    if (isAuthenticated || checkAuthToken()) {
      navigate('/dashboard');
      return;
    }
    redirectToAuth();
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-3 md:py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Heart className="h-6 w-6 md:h-8 md:w-8 text-primary" />
            <span className="text-lg md:text-2xl font-bold">healthscores.ai</span>
          </div>
          <div className="flex gap-2 md:gap-4">
            <Link to="/employers">
              <Button variant="outline" size="sm" className="md:h-10 md:px-4 md:py-2">
                <Building2 className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">For Employers</span>
              </Button>
            </Link>
            <Button onClick={handleAuthAction} size="sm" className="md:h-10 md:px-4 md:py-2">
              {isAuthenticated ? "Dashboard" : "Sign In"}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 md:py-16 lg:py-20 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Your Health, Simplified
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 md:mb-8 max-w-2xl mx-auto px-4">
            Get a comprehensive view of your health with our intuitive Health Score.
            Connect your medical records, wearables, and family caregiving in one secure platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <Button size="lg" className="w-full sm:w-auto px-6 md:px-8" onClick={handleAuthAction}>
              {isAuthenticated ? "Go to Dashboard" : "Get Started"}
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto px-6 md:px-8">
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-16 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 md:mb-4">Why Choose healthscores.ai?</h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto px-4">
              Understanding your health should be as simple as checking your credit score.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <Card className="text-center">
              <CardHeader>
                <TrendingUp className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>Health Score</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Get a simple 0-1000 score that reflects your overall health status with clear insights.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>Secure & Private</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  HIPAA-compliant platform with end-to-end encryption for your sensitive health data.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <Users className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>Care Circle</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Connect with family members and caregivers to share health information securely.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <Heart className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>Comprehensive</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Integrate with health systems, wearables, and manual tracking in one place.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 md:py-16 px-4">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 md:mb-6">Take Control of Your Health Journey</h2>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-start space-x-3">
                  <Check className="h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm md:text-base font-semibold">Connect Your Data Sources</h3>
                    <p className="text-sm text-muted-foreground">Link hospital portals, Apple Health, Google Fit, and more.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm md:text-base font-semibold">Get Actionable Insights</h3>
                    <p className="text-sm text-muted-foreground">Receive personalized recommendations and trend alerts.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm md:text-base font-semibold">Share with Providers</h3>
                    <p className="text-sm text-muted-foreground">Easily share health data with your healthcare team.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm md:text-base font-semibold">Family Health Management</h3>
                    <p className="text-sm text-muted-foreground">Manage your entire family's health in one dashboard.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:text-center mt-8 lg:mt-0">
              <div className="relative">
                <div className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 mx-auto bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full flex items-center justify-center">
                  <div className="text-5xl sm:text-6xl font-bold text-primary">850</div>
                </div>
                <div className="mt-3 md:mt-4 text-center">
                  <h3 className="text-lg md:text-xl font-semibold">Your Health Score</h3>
                  <p className="text-sm md:text-base text-muted-foreground">Excellent Health</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 px-4 bg-primary text-primary-foreground">
        <div className="container mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 md:mb-4">Ready to Get Started?</h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 md:mb-8 opacity-90 px-4">
            Join thousands of users who are taking control of their health journey.
          </p>
          <Button size="lg" variant="secondary" className="w-full sm:w-auto px-6 md:px-8" onClick={handleAuthAction}>
            Start Your Health Journey
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-6 md:py-8 px-4 bg-background">
        <div className="container mx-auto text-center text-muted-foreground">
          <p className="text-sm md:text-base">&copy; 2025 healthscores.ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
